import React, { useEffect, useState } from "react";
import { Modal, Table } from "react-bootstrap";
import { api } from "../services/api";
import PageHeader from "./ui/PageHeader";
import DataCard from "./ui/DataCard";
import LoadingState from "./ui/LoadingState";
import AlertBanner from "./ui/AlertBanner";

const RBAC = () => {
  const [roles, setRoles] = useState([]);
  const [staffLogs, setStaffLogs] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newRole, setNewRole] = useState("");
  const [newPermissions, setNewPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => { loadRbac(); }, []);

  const loadRbac = async () => {
    setLoading(true);
    try {
      const data = await api.getRbac();
      setRoles(data.roles);
      setStaffLogs(data.staffLogs);
      setAllPermissions(data.allPermissions);
    } catch (err) {
      setFeedback({ type: "danger", message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleAddRole = async () => {
    if (!newRole.trim() || !newPermissions.length) {
      setFeedback({ type: "warning", message: "Role name and permissions required." });
      return;
    }
    setSaving(true);
    try {
      const created = await api.createRole(newRole.trim(), newPermissions);
      setRoles((prev) => [...prev, created]);
      setNewRole("");
      setNewPermissions([]);
      setShowModal(false);
      setFeedback({ type: "success", message: `Role "${created.name}" created.` });
    } catch (err) {
      setFeedback({ type: "danger", message: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState message="Loading RBAC..." />;

  return (
    <div>
      <PageHeader
        title="Role-Based Access Control"
        subtitle="Manage staff roles and permissions"
        action={
          <button className="btn-billr btn-billr-primary" onClick={() => setShowModal(true)}>+ Create Role</button>
        }
      />

      {feedback && <AlertBanner type={feedback.type} message={feedback.message} onClose={() => setFeedback(null)} />}

      <DataCard title="Roles & Permissions">
        <Table responsive className="billr-table mb-0">
          <thead><tr><th>Role</th><th>Permissions</th></tr></thead>
          <tbody>
            {roles.map((role) => (
              <tr key={role.id || role.name}>
                <td><strong>{role.name}</strong></td>
                <td>{role.permissions.join(", ") || "—"}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </DataCard>

      <DataCard title="Staff Activity">
        <Table responsive className="billr-table mb-0">
          <thead><tr><th>Staff</th><th>Action</th><th>Time</th></tr></thead>
          <tbody>
            {staffLogs.map((log, idx) => (
              <tr key={idx}><td>{log.name}</td><td>{log.action}</td><td className="text-muted">{log.timestamp}</td></tr>
            ))}
          </tbody>
        </Table>
      </DataCard>

      <Modal show={showModal} onHide={() => !saving && setShowModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Create Role</Modal.Title></Modal.Header>
        <Modal.Body>
          <label className="small fw-semibold text-muted">Role name</label>
          <input className="billr-input mb-3" value={newRole} onChange={(e) => setNewRole(e.target.value)} />
          <label className="small fw-semibold text-muted">Permissions</label>
          <div className="d-flex flex-wrap gap-2">
            {allPermissions.map((perm) => (
              <label key={perm} className="billr-badge secondary" style={{ cursor: "pointer", opacity: newPermissions.includes(perm) ? 1 : 0.5 }}>
                <input type="checkbox" className="me-1" checked={newPermissions.includes(perm)} onChange={() => setNewPermissions((p) => p.includes(perm) ? p.filter((x) => x !== perm) : [...p, perm])} />
                {perm}
              </label>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn-billr btn-billr-outline" onClick={() => setShowModal(false)}>Cancel</button>
          <button className="btn-billr btn-billr-primary" disabled={saving} onClick={handleAddRole}>{saving ? "Saving..." : "Create"}</button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RBAC;
